import { X } from "lucide-react";
import { type ReactNode, useEffect } from "react";

import { cn } from "@/lib/utils";

// A compact-width sheet: `bottom` slides up from the bottom edge (calendar
// list), `full` covers the surface (event detail). Both pad for the safe
// areas because they are fixed to the viewport, outside the body padding.
export function Sheet({
  title,
  variant,
  onClose,
  children,
  label,
  testId,
}: {
  title: string;
  variant: "bottom" | "full";
  onClose: () => void;
  children: ReactNode;
  label?: string;
  testId?: string;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" data-calendar-sheet={testId}>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className={cn(
          "absolute inset-0 bg-foreground/30 backdrop-blur-[1px]",
          variant === "full" && "hidden",
        )}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-label={label ?? title}
        className={cn(
          "relative flex flex-col bg-background text-foreground shadow-2xl",
          variant === "bottom" ? "max-h-[85dvh] rounded-t-2xl border-t border-border/70" : "h-full",
        )}
        style={{
          paddingBottom: "env(safe-area-inset-bottom)",
          paddingLeft: "env(safe-area-inset-left)",
          paddingRight: "env(safe-area-inset-right)",
          paddingTop: variant === "full" ? "env(safe-area-inset-top)" : undefined,
        }}
      >
        <header className="flex items-center gap-2 border-b border-border/70 px-4 py-2">
          <h2 className="min-w-0 flex-1 truncate font-display text-lg tracking-tight">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            aria-label="Close"
            className="flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X aria-hidden className="h-5 w-5" />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </section>
    </div>
  );
}
